import { useMemo, useState } from "react";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogTitle from "@mui/material/DialogTitle";
import { Box, DialogContent, FormControl, InputLabel, MenuItem } from "@mui/material";
import { P } from "theme/typography";
import PSelect from "components/PSelect";
import { selectActiveUsers, selectUsers, updateUserDeactivated } from "redux/slices/client";
import { useAppSelector } from "redux/hooks";
import { LoadingButton } from "@mui/lab";
import { useNavigate } from "react-router-dom";
import useAppDispatchWithSnackbar from "helpers/hooks/useAppDispatchWithSnackbar";
import { useSnackbar } from "notistack";
import { fullName } from "helpers/name-helpers";
import theme from "theme";
import { UserInfo } from "generated/graphql";

export interface DeactivateUserDialogProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  user: UserInfo;
}
const DeactivateUserDialog = (props: DeactivateUserDialogProps) => {
  const { user } = props;
  const userId = user.id;
  const userDeactivated = user.deactivated;
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();

  const [assignee, setAssignee] = useState<number | undefined | "unassigned">(undefined);
  const [pendingDeactivate, setPendingDeactivate] = useState(false);
  const activeUsers = useAppSelector(selectActiveUsers);
  const users = useAppSelector(selectUsers);
  const snackpatch = useAppDispatchWithSnackbar();
  const filteredUsers = useMemo(() => activeUsers.filter((user) => user.id !== userId), [users, userId]);
  const handleClose = () => {
    setAssignee(undefined);
    props.setOpen(false);
  };

  const handleUpdateDeactivate = (deactivate: boolean) => {
    setPendingDeactivate(true);
    if (userId) {
      snackpatch(
        updateUserDeactivated({
          userId: userId,
          deactivated: deactivate,
          newCaseAssignee: assignee === "unassigned" ? undefined : assignee,
        }),
        {
          successMessage: deactivate ? "User deactivated" : "User activated",
        }
      )
        .then(() => {
          setPendingDeactivate(false);
          handleClose();
          navigate("../profile");
        })
        .catch((gqlError) => {
          enqueueSnackbar(gqlError?.message ?? "Error deactivating user", { variant: "error" });
          setPendingDeactivate(false);
        });
    }
  };

  return (
    <Dialog open={props.open} onClose={() => props.setOpen(false)}>
      <DialogTitle>
        {userDeactivated ? "Reactivate" : "Deactivate"} {fullName(user)}
      </DialogTitle>
      {userDeactivated ? (
        <DialogContent>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 2,
            }}>
            <P variant="sm">
              Are you sure you want to reactivate <b>{fullName(user)}</b> (<b>{user.email}</b>)? They will be granted
              access to the lenses they previously had access to.
            </P>
          </Box>
        </DialogContent>
      ) : (
        <DialogContent>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 2,
            }}>
            <P variant="sm">
              Are you sure you want to deactivate <b>{fullName(user)}</b> (<b>{user.email}</b>) and reassign their
              cases?
            </P>
            <P variant="sm">
              Please select the new assignee for the deactivated user&apos;s cases. You can also choose to keep the
              cases unassigned. This action cannot be undone.
            </P>
          </Box>
          <FormControl size="small" fullWidth sx={{ mt: 2 }}>
            <InputLabel variant="outlined">Select New Assignee</InputLabel>
            <PSelect
              label="Select New Assignee"
              value={assignee}
              onChange={(e) => {
                const value = e.target.value;
                if (typeof value === "number" || value === "unassigned") {
                  setAssignee(value);
                } else {
                  setAssignee(undefined);
                }
              }}>
              <MenuItem key="unassigned" value="unassigned" sx={{ py: 2 }}>
                Unassigned
              </MenuItem>
              {filteredUsers.map((user) => (
                <MenuItem key={user.id} value={user.id}>
                  <Box sx={{ display: "flex", flexDirection: "column" }}>
                    <P>{fullName(user)}</P>
                    <P variant="xs" sx={{ color: theme.palette.grey[400] }}>
                      {user.email}
                    </P>
                  </Box>
                </MenuItem>
              ))}
            </PSelect>
          </FormControl>
        </DialogContent>
      )}

      <DialogActions>
        <Button onClick={handleClose}>Cancel</Button>
        <LoadingButton
          variant="contained"
          onClick={() => handleUpdateDeactivate(!userDeactivated)}
          disabled={userDeactivated ? false : assignee === undefined}
          loading={pendingDeactivate}>
          {userDeactivated ? "Reactivate" : "Deactivate"}
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
};

export default DeactivateUserDialog;
